import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import {
    mailApi,
    type SendMessagePayload,
    type UpsertMailIdentityPayload,
    type UpsertDraftPayload,
    type UpsertLabelPayload,
    type UpsertFilterPayload,
    type UpsertAutoResponderPayload,
    type UpsertTemplatePayload,
    type VacationResponder,
    type UpsertContactPayload,
    type UpsertTaskPayload,
    type UpsertTaskListPayload,
    type TaskItem,
    type TaskList,
    type AiChatRequest,
    type Folder,
    type MessagePage,
    type MessageSummary,
    type RelocateResult,
    type SearchResult
} from "./mailApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { mailPollInterval } from "@/lib/mailPush";

// Exported so prefetch helpers (e.g. MailPage hover-prefetch) can build
// the same cache key shape this hook does. Don't reorder fields without
// updating every queryClient.invalidateQueries call across the codebase.
export const keys = {
    folders: (token: string | undefined) => ["folders", token] as const,
    messages: (folderId: string, token: string | undefined) => ["messages", folderId, token] as const,
    message: (id: string, token: string | undefined) => ["message", id, token] as const,
    identities: (token: string | undefined) => ["identities", token] as const,
    drafts: (token: string | undefined) => ["drafts", token] as const,
    draft: (id: string, token: string | undefined) => ["draft", id, token] as const,
    tasks: (token: string | undefined) => ["tasks", token] as const
};

export function useFolders() {
    const token = useAccessToken();
    return useQuery({
        queryKey: keys.folders(token),
        queryFn: () => mailApi.listFolders(token),
        enabled: Boolean(token),
        // Poll so the inbox unread count updates when new mail arrives — this is
        // what drives the new-mail toast + desktop notification in MailPage.
        // Without it the count only refreshed on an explicit action, so a
        // freshly-arrived message showed in the list (useMessages polls) but
        // never triggered a notification.
        // The push stream says when something changed; this poll is the safety net (and the fallback if push is down).
        refetchInterval: () => mailPollInterval(),
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true
    });
}

/** Messages per batch in the list; the next batch loads as you scroll near the end. */
export const MESSAGE_PAGE_SIZE = Math.max(10, Number(import.meta.env.VITE_MAIL_PAGE_SIZE) || 50);

/** Query key of a folder's scroll-loaded list (also used by the folder hover-prefetch). */
export const messageStreamKey = (folderId: string, token: string | undefined) =>
    [...keys.messages(folderId, token), "stream"] as const;

/**
 * A folder as one scroll-loaded list (infinite scroll): the first batch, then
 * the next each time MessageList's sentinel nears the view. Every page carries
 * the folder's real total, so "has more" is exact.
 */
export function useMessages(folderId: string) {
    const token = useAccessToken();
    return useInfiniteQuery({
        queryKey: messageStreamKey(folderId, token),
        queryFn: ({ pageParam }) => mailApi.listMessagesPage(folderId, pageParam, MESSAGE_PAGE_SIZE, token),
        initialPageParam: 1,
        getNextPageParam: (last, pages) => (pages.length * MESSAGE_PAGE_SIZE < last.total ? pages.length + 1 : undefined),
        enabled: Boolean(folderId) && Boolean(token),
        // The BFF reads the IMAP folder live on each call (no DB/sync), but the
        // SPA still has to ask — so poll every 20s and on tab-focus/reconnect.
        // Without this the inbox only refreshed on load/folder-switch, so mail
        // that landed after open didn't appear until a manual reload. (For true
        // phone-like instant delivery we'd add IMAP IDLE + a SignalR push; this
        // near-real-time poll is the cheap first step.) A poll or focus refetch
        // re-reads EVERY loaded batch from IMAP; deep in a folder (more than
        // three batches) that is many slow round-trips, so it stops there — the
        // folders poll still raises the new-mail toast, and Refresh starts over.
        refetchInterval: (query) => ((query.state.data?.pages.length ?? 1) > 3 ? false : mailPollInterval()),
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: (query) => (query.state.data?.pages.length ?? 1) <= 3,
        refetchOnReconnect: true
    });
}

export function useMessage(id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: id ? keys.message(id, token) : ["message", "none"],
        queryFn: () => (id ? mailApi.getMessage(id, token) : Promise.reject(new Error("no id"))),
        enabled: Boolean(id) && Boolean(token)
    });
}

/**
 * RFC 3798 inbound: send the requested Message Disposition Notification
 * for an incoming message that asked for a read receipt. Idempotent.
 */
export function useSendReadReceipt() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (messageId: string) => mailApi.sendReadReceipt(messageId, token),
        onSuccess: () => invalidateMailLists(qc)
    });
}

/**
 * Mark the receipt prompt as handled WITHOUT sending an MDN reply.
 */
export function useDismissReadReceipt() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (messageId: string) => mailApi.dismissReadReceipt(messageId, token),
        onSuccess: () => invalidateMailLists(qc)
    });
}

/**
 * Sibling messages in the same thread as the given message id.
 * Returns chronologically-ordered MessageSummary[] (oldest first), one
 * row per message — INCLUDING the anchor message itself. The reading
 * pane uses this to render the "Conversation (N)" panel.
 */
export function useThreadByMessage(messageId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: messageId ? ["thread-by-message", messageId, token ?? "anon"] : ["thread-by-message", "none"],
        queryFn: () =>
            messageId
                ? mailApi.listThreadByMessage(messageId, token)
                : Promise.reject(new Error("no id")),
        enabled: Boolean(messageId) && Boolean(token),
        // A missing thread is expected (message moved/expunged) — don't retry,
        // which previously spammed the console with three 404s per selection.
        retry: false
    });
}

export function useSendMessage() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: SendMessagePayload) => mailApi.sendMessage(payload, token),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["messages"] });
            void qc.invalidateQueries({ queryKey: ["folders"] });
        },
        onError: (err: unknown) => {
            console.error("useSendMessage error:", err);
        }
    });
}

export function useUploadAttachment() {
    const token = useAccessToken();
    return useMutation({
        mutationFn: (file: File) => mailApi.uploadAttachment(file, token)
    });
}

export function useIdentities() {
    const token = useAccessToken();
    return useQuery({
        queryKey: keys.identities(token),
        queryFn: () => mailApi.listIdentities(token),
        enabled: Boolean(token)
    });
}

export function useCreateIdentity() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: UpsertMailIdentityPayload) => mailApi.createIdentity(payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["identities"] })
    });
}

export function useUpdateIdentity() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertMailIdentityPayload }) =>
            mailApi.updateIdentity(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["identities"] })
    });
}

export function useDeleteIdentity() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteIdentity(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["identities"] })
    });
}

export function useSetDefaultIdentity() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.setDefaultIdentity(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["identities"] })
    });
}

export function useDrafts() {
    const token = useAccessToken();
    return useQuery({
        queryKey: keys.drafts(token),
        queryFn: () => mailApi.listDrafts(token),
        enabled: Boolean(token)
    });
}

export function useSaveDraft() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id?: string | null; payload: UpsertDraftPayload }) =>
            args.id ? mailApi.updateDraft(args.id, args.payload, token) : mailApi.createDraft(args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["drafts"] })
    });
}

export function useDeleteDraft() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteDraft(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["drafts"] })
    });
}

function invalidateMailLists(qc: ReturnType<typeof useQueryClient>) {
    void qc.invalidateQueries({ queryKey: ["messages"] });
    void qc.invalidateQueries({ queryKey: ["folders"] });
    void qc.invalidateQueries({ queryKey: ["message"] });
    void qc.invalidateQueries({ queryKey: ["search"] });
    void qc.invalidateQueries({ queryKey: ["thread-by-message"] });
}

// ---------- Optimistic list updates ----------
// Read / unread / flag / move each opened an IMAP connection, changed one flag
// and then waited for a full re-read of the folder before the list changed —
// a second or more per click. The cached lists, folder unread counts and the
// open message now change at once; the server catches up in the background,
// the lists are re-read when it's done, and a failure rolls everything back.

type MailSnapshot = Array<[readonly unknown[], unknown]>;
type OptimisticKind = "read" | "unread" | "flag" | "unflag" | "pin" | "unpin" | "remove";

function snapshotMail(qc: QueryClient): MailSnapshot {
    return [
        ...qc.getQueriesData({ queryKey: ["messages"] }),
        ...qc.getQueriesData({ queryKey: ["folders"] }),
        ...qc.getQueriesData({ queryKey: ["message"] }),
        ...qc.getQueriesData({ queryKey: ["search"] })
    ] as MailSnapshot;
}

function restoreMail(qc: QueryClient, snapshot: MailSnapshot) {
    for (const [key, data] of snapshot) qc.setQueryData(key, data);
}

function patchFlags<T>(m: T, kind: OptimisticKind): T {
    switch (kind) {
        case "read": return { ...m, isRead: true };
        case "unread": return { ...m, isRead: false };
        case "flag": return { ...m, isFlagged: true };
        case "unflag": return { ...m, isFlagged: false };
        case "pin": return { ...m, isPinned: true };
        case "unpin": return { ...m, isPinned: false };
        default: return m;
    }
}

function applyOptimistic(qc: QueryClient, ids: string[], kind: OptimisticKind) {
    const wanted = new Set(ids);
    const counted = new Set<string>();
    const unreadDelta = new Map<string, number>();
    const bump = (folderId: string, by: number) => unreadDelta.set(folderId, (unreadDelta.get(folderId) ?? 0) + by);

    const patchPage = (page: MessagePage): MessagePage => {
        if (!Array.isArray(page.items)) return page;
        let removed = 0;
        const items: MessageSummary[] = [];
        for (const m of page.items) {
            if (!wanted.has(m.id)) { items.push(m); continue; }
            const first = !counted.has(m.id);
            counted.add(m.id);
            if (kind === "remove") {
                removed++;
                if (first && !m.isRead) bump(m.folderId, -1);
                continue;
            }
            if (first && kind === "read" && !m.isRead) bump(m.folderId, -1);
            if (first && kind === "unread" && m.isRead) bump(m.folderId, 1);
            items.push(patchFlags(m, kind));
        }
        return { ...page, items, total: Math.max(0, page.total - removed) };
    };
    // A folder list is either one page or the scroll-loaded stream of pages
    // (useMessages → useInfiniteQuery: { pages, pageParams }).
    qc.setQueriesData<MessagePage | InfiniteData<MessagePage, number>>({ queryKey: ["messages"] }, (old) => {
        if (!old) return old;
        if ("pages" in old && Array.isArray(old.pages)) {
            const pages = old.pages.map(patchPage);
            // Every page reports the folder total; keep them in step after a removal.
            const total = pages.length > 0 ? Math.min(...pages.map((p) => p.total)) : 0;
            return { ...old, pages: pages.map((p) => ({ ...p, total })) };
        }
        return "items" in old ? patchPage(old) : old;
    });

    // Search results change in step with the folder lists.
    qc.setQueriesData<SearchResult>({ queryKey: ["search"] }, (old) => {
        if (!old || !Array.isArray(old.messages)) return old;
        if (kind === "remove") {
            const messages = old.messages.filter((m) => !wanted.has(m.id));
            return { ...old, messages, totalHits: Math.max(0, old.totalHits - (old.messages.length - messages.length)) };
        }
        return { ...old, messages: old.messages.map((m) => (wanted.has(m.id) ? patchFlags(m, kind) : m)) };
    });

    if (unreadDelta.size > 0) {
        qc.setQueriesData<Folder[]>({ queryKey: ["folders"] }, (old) =>
            old?.map((f) => {
                const by = unreadDelta.get(f.id);
                return by ? { ...f, unreadCount: Math.max(0, f.unreadCount + by) } : f;
            }));
    }
    if (kind !== "remove") {
        for (const id of ids) {
            qc.setQueriesData<Record<string, unknown>>({ queryKey: ["message", id] }, (old) =>
                (old ? patchFlags(old, kind) : old));
        }
    }
}

export function useMessageAction() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        onMutate: async (args) => {
            await qc.cancelQueries({ queryKey: ["messages"] });
            const snapshot = snapshotMail(qc);
            const kind: OptimisticKind = args.type === "archive" || args.type === "trash"
                || args.type === "delete" || args.type === "move"
                ? "remove"
                : args.type;
            applyOptimistic(qc, [args.id], kind);
            return { snapshot };
        },
        onError: (_err, _args, context) => {
            if (context) restoreMail(qc, context.snapshot);
        },
        // Archive / trash / move go through /relocate, which reports the
        // message's new id — callers use it to offer Undo (z).
        mutationFn: async (args:
            | { type: "read" | "unread"; id: string }
            | { type: "flag" | "unflag"; id: string }
            | { type: "pin" | "unpin"; id: string }
            | { type: "archive" | "trash" | "delete"; id: string }
            | { type: "move"; id: string; folderId: string }): Promise<RelocateResult | void> => {
            switch (args.type) {
                case "read":
                    return mailApi.setRead(args.id, true, token);
                case "unread":
                    return mailApi.setRead(args.id, false, token);
                case "flag":
                    return mailApi.setFlagged(args.id, true, token);
                case "unflag":
                    return mailApi.setFlagged(args.id, false, token);
                case "pin":
                    return mailApi.setPinned(args.id, true, token);
                case "unpin":
                    return mailApi.setPinned(args.id, false, token);
                case "archive":
                    return mailApi.relocate(args.id, "archive", token);
                case "trash":
                    return mailApi.relocate(args.id, "trash", token);
                case "delete":
                    return mailApi.deleteMessage(args.id, token);
                case "move":
                    return mailApi.relocate(args.id, args.folderId, token);
            }
        },
        // Re-read from the server once it's done, success or failure.
        onSettled: () => invalidateMailLists(qc)
    });
}

export function useBulkMessageAction() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: {
            action: "read" | "unread" | "archive" | "trash" | "delete" | "move";
            ids: string[];
            folderId?: string;
        }) => mailApi.bulkMessages(args.action, args.ids, token, args.folderId),
        onMutate: async (args) => {
            await qc.cancelQueries({ queryKey: ["messages"] });
            const snapshot = snapshotMail(qc);
            applyOptimistic(qc, args.ids, args.action === "read" || args.action === "unread" ? args.action : "remove");
            return { snapshot };
        },
        onError: (_err, _args, context) => {
            if (context) restoreMail(qc, context.snapshot);
        },
        onSettled: () => invalidateMailLists(qc)
    });
}

export function useSearch(params: import("./mailApi").SearchParams, enabled: boolean) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["search", params, token],
        queryFn: () => mailApi.search(params, token),
        enabled: enabled && Boolean(token),
        // Paging the same query keeps the current page on screen while the
        // next loads; a new query starts clean.
        placeholderData: (previous, previousQuery) =>
            (previousQuery?.queryKey[1] as import("./mailApi").SearchParams | undefined)?.query === params.query
                ? previous
                : undefined
    });
}

/**
 * Header-chain thread keys for the rows on screen (conversation view). The
 * BFF answers an empty map on non-IMAP stores; the list then groups by
 * normalised subject.
 */
// Thread keys already learned, per signed-in user: scrolling to the next batch asks only about the NEW rows
// (it used to re-ask about every loaded row, each a header search on the mail server, on every batch).
const threadKeyCache = new Map<string, { keys: Record<string, string>; asked: Set<string> }>();
function threadKeyCacheFor(token: string | undefined) {
    const k = token ?? "anon";
    let c = threadKeyCache.get(k);
    if (!c) { c = { keys: {}, asked: new Set() }; threadKeyCache.set(k, c); }
    return c;
}

export function useThreadKeys(ids: string[], enabled: boolean) {
    const token = useAccessToken();
    const cache = threadKeyCacheFor(token);
    const [version, setVersion] = useState(0);
    const idsKey = ids.join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const missing = useMemo(() => ids.filter((id) => !cache.asked.has(id)), [idsKey, version, cache]);
    const q = useQuery({
        queryKey: ["thread-keys", token, missing.join("|")],
        queryFn: async () => {
            const found = await mailApi.threadKeys([...missing].sort(), token);
            Object.assign(cache.keys, found);
            for (const id of missing) cache.asked.add(id);
            return found;
        },
        enabled: enabled && missing.length > 0 && Boolean(token),
        staleTime: Infinity,
        gcTime: 60_000,
        retry: false
    });
    useEffect(() => { if (q.data) setVersion((v) => v + 1); }, [q.data]);
    // A new object only when something was learned, so the grouping memo downstream doesn't recompute for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = useMemo(() => (enabled ? { ...cache.keys } : undefined), [enabled, version, cache]);
    return { data, isLoading: q.isLoading, isError: q.isError };
}

// ---------- Labels ----------

export function useLabels() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["labels", token],
        queryFn: () => mailApi.listLabels(token),
        enabled: Boolean(token)
    });
}

export function useCreateLabel() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: UpsertLabelPayload) => mailApi.createLabel(payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["labels"] })
    });
}

export function useUpdateLabel() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertLabelPayload }) =>
            mailApi.updateLabel(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["labels"] })
    });
}

export function useDeleteLabel() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteLabel(id, token),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["labels"] });
            void qc.invalidateQueries({ queryKey: ["message-labels"] });
        }
    });
}

export function useMessageLabels(messageId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["message-labels", messageId, token],
        queryFn: () =>
            messageId ? mailApi.listMessageLabels(messageId, token) : Promise.resolve([]),
        enabled: Boolean(messageId && token)
    });
}

export function useApplyLabel() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { messageId: string; labelId: string; apply: boolean }) =>
            args.apply
                ? mailApi.applyMessageLabel(args.messageId, args.labelId, token)
                : mailApi.unapplyMessageLabel(args.messageId, args.labelId, token),
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: ["message-labels", vars.messageId] });
        }
    });
}

// ---------- Filters ----------

export function useFilters() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["filters", token],
        queryFn: () => mailApi.listFilters(token),
        enabled: Boolean(token)
    });
}

export function useCreateFilter() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertFilterPayload) => mailApi.createFilter(p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["filters"] })
    });
}

export function useUpdateFilter() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertFilterPayload }) =>
            mailApi.updateFilter(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["filters"] })
    });
}

export function useDeleteFilter() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteFilter(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["filters"] })
    });
}

export function useApplyFilter() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.applyFilter(id, token),
        // Moving existing mail changes folder message lists + counts.
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["messages"] });
            void qc.invalidateQueries({ queryKey: ["folders"] });
        }
    });
}

// ---------- Auto-responders ----------

export function useAutoResponders() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["auto-responders", token],
        queryFn: () => mailApi.listAutoResponders(token),
        enabled: Boolean(token)
    });
}

export function useCreateAutoResponder() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertAutoResponderPayload) => mailApi.createAutoResponder(p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["auto-responders"] })
    });
}

export function useUpdateAutoResponder() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertAutoResponderPayload }) =>
            mailApi.updateAutoResponder(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["auto-responders"] })
    });
}

export function useDeleteAutoResponder() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteAutoResponder(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["auto-responders"] })
    });
}

// ---------- Vacation ----------

export function useVacation() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["vacation", token],
        queryFn: () => mailApi.getVacation(token),
        enabled: Boolean(token)
    });
}

export function usePutVacation() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: VacationResponder) => mailApi.putVacation(payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["vacation"] })
    });
}

// ---------- Templates ----------

export function useTemplates() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["templates", token],
        queryFn: () => mailApi.listTemplates(token),
        enabled: Boolean(token)
    });
}

export function useCreateTemplate() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertTemplatePayload) => mailApi.createTemplate(p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["templates"] })
    });
}

export function useUpdateTemplate() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertTemplatePayload }) =>
            mailApi.updateTemplate(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["templates"] })
    });
}

export function useDeleteTemplate() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteTemplate(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["templates"] })
    });
}

// ---------- Snooze ----------

export function useSnoozeMessage() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { messageId: string; wakeAt: string }) =>
            mailApi.snoozeMessage(args.messageId, args.wakeAt, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["snoozes"] })
    });
}

export function useSnoozes() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["snoozes", token],
        queryFn: () => mailApi.listSnoozes(token),
        enabled: Boolean(token)
    });
}

export function useScheduleSend() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: {
            to: string;
            cc?: string;
            bcc?: string;
            subject: string;
            bodyHtml: string;
            attachmentIds: string[];
            sendAt: string;
        }) => mailApi.scheduleSend(args, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["scheduled-sends"] })
    });
}

// ---------- Contacts ----------

export function useContacts() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["contacts", token],
        queryFn: () => mailApi.listContacts(token),
        enabled: Boolean(token)
    });
}

export function useCreateContact() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: UpsertContactPayload) => mailApi.createContact(payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] })
    });
}

export function useUpdateContact() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertContactPayload }) =>
            mailApi.updateContact(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] })
    });
}

export function useDeleteContact() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteContact(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] })
    });
}

export function useToggleFavoriteContact() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertContactPayload }) =>
            mailApi.updateContact(args.id, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] })
    });
}

// ---------- Folder management ----------

export function useCreateFolder() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { name: string; parentId?: string | null }) =>
            mailApi.createFolder(args.name, args.parentId ?? null, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["folders"] })
    });
}

export function useDeleteFolder() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (folderId: string) => mailApi.deleteFolder(folderId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["folders"] })
    });
}

export function useMarkAllRead() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (folderId: string) => mailApi.markAllRead(folderId, token),
        onSuccess: (_data, folderId) => {
            void qc.invalidateQueries({ queryKey: ["messages", folderId] });
            void qc.invalidateQueries({ queryKey: ["folders"] });
        }
    });
}

export function useEmptyTrash() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: () => mailApi.emptyTrash(token),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["messages", "trash"] });
            void qc.invalidateQueries({ queryKey: ["folders"] });
        }
    });
}

export function useAiChat() {
    const token = useAccessToken();
    return useMutation({
        mutationFn: (req: AiChatRequest) => mailApi.aiChat(req, token)
    });
}

// Free machine translation — used by the reading-pane Translate bar. Separate
// from useAiChat so it works without a Claude/Anthropic backend.
export function useTranslate() {
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { text: string; targetLang: string }) =>
            mailApi.translate(args.text, args.targetLang, token)
    });
}

// Layout-preserving Translate: the reading pane sends the email's text pieces
// and writes each translation back into the original HTML.
export function useTranslateSegments() {
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { segments: string[]; targetLang: string }) =>
            mailApi.translateSegments(args.segments, args.targetLang, token)
    });
}

export function useCalendarSettings() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["calendar-settings", token],
        queryFn: () => mailApi.getCalendarSettings(token),
        enabled: Boolean(token)
    });
}

export function usePutCalendarSettings() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        // Partial: only the fields passed change on the server.
        mutationFn: (patch: Parameters<typeof mailApi.putCalendarSettings>[0]) => mailApi.putCalendarSettings(patch, token),
        onSuccess: (data) => qc.setQueryData(["calendar-settings", token], data)
    });
}

export function useSafeSenders() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["safe-senders", token],
        queryFn: () => mailApi.listSafeSenders(token),
        enabled: Boolean(token),
        staleTime: 5 * 60_000
    });
}

export function useAddSafeSender() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (address: string) => mailApi.addSafeSender(address, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["safe-senders"] })
    });
}

export function useRemoveSafeSender() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.removeSafeSender(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["safe-senders"] })
    });
}

// ──────────────────────────────────────────────────────────────────────
// Tasks (Outlook / Gmail "Tasks" equivalent — see TaskEndpoints on BFF)
// ──────────────────────────────────────────────────────────────────────

// Every task cache (["tasks", token, false | true | "all"]) is patched at
// once so the rail badge, reminders and the Tasks page stay in step; the
// server's copy replaces the optimistic one when it answers, and a failure
// restores the snapshot.

export function useTasks(completed?: boolean) {
    const token = useAccessToken();
    return useQuery({
        queryKey: [...keys.tasks(token), completed ?? "all"],
        queryFn: () => mailApi.listTasks(token, completed),
        enabled: Boolean(token),
        // Reminders and "flagged email" changes made elsewhere show up.
        refetchInterval: 60_000,
        refetchOnWindowFocus: true
    });
}

type TaskSnapshot = Array<[readonly unknown[], TaskItem[] | undefined]>;

function snapshotTasks(qc: QueryClient): TaskSnapshot {
    return qc.getQueriesData<TaskItem[]>({ queryKey: ["tasks"] }) as TaskSnapshot;
}

function restoreTasks(qc: QueryClient, snapshot: TaskSnapshot | undefined) {
    if (!snapshot) return;
    for (const [key, data] of snapshot) qc.setQueryData(key, data);
}

/** Upsert / remove tasks in every task cache, honouring each cache's completed filter. */
export function applyTaskChange(qc: QueryClient, change: { upsert?: TaskItem[]; remove?: string[] }) {
    const remove = new Set(change.remove ?? []);
    for (const [key, data] of qc.getQueriesData<TaskItem[]>({ queryKey: ["tasks"] })) {
        if (!Array.isArray(data)) continue;
        let next = data.filter((t) => !remove.has(t.id));
        for (const u of change.upsert ?? []) {
            const i = next.findIndex((t) => t.id === u.id);
            if (i >= 0) next[i] = u;
            else next.push(u);
        }
        const filter = key[2];
        if (typeof filter === "boolean") next = next.filter((t) => t.isCompleted === filter);
        qc.setQueryData(key, next);
    }
}

export function findCachedTask(qc: QueryClient, id: string): TaskItem | undefined {
    for (const [, data] of qc.getQueriesData<TaskItem[]>({ queryKey: ["tasks"] })) {
        const hit = Array.isArray(data) ? data.find((t) => t.id === id) : undefined;
        if (hit) return hit;
    }
    return undefined;
}

export function taskToPayload(t: TaskItem): UpsertTaskPayload {
    return {
        title: t.title,
        notes: t.notes,
        dueAt: t.dueAt,
        reminderAt: t.reminderAt,
        recurrence: t.recurrence,
        isImportant: t.isImportant,
        myDayDate: t.myDayDate,
        listId: t.listId,
        steps: t.steps,
        relatedMessageId: t.relatedMessageId
    };
}

export function useCreateTask() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: UpsertTaskPayload) => mailApi.createTask(payload, token),
        onMutate: async (payload) => {
            await qc.cancelQueries({ queryKey: ["tasks"] });
            const snapshot = snapshotTasks(qc);
            const now = new Date().toISOString();
            const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            applyTaskChange(qc, {
                upsert: [{
                    id: tempId,
                    title: payload.title,
                    notes: payload.notes ?? null,
                    dueAt: payload.dueAt ?? null,
                    isCompleted: false,
                    completedAt: null,
                    createdAt: now,
                    updatedAt: now,
                    relatedMessageId: payload.relatedMessageId ?? null,
                    listId: payload.listId ?? null,
                    isImportant: payload.isImportant ?? false,
                    reminderAt: payload.reminderAt ?? null,
                    recurrence: payload.recurrence ?? null,
                    myDayDate: payload.myDayDate ?? null,
                    steps: payload.steps ?? [],
                    source: "manual"
                }]
            });
            return { snapshot, tempId };
        },
        onError: (_e, _p, ctx) => restoreTasks(qc, ctx?.snapshot),
        onSuccess: (task, _p, ctx) => applyTaskChange(qc, { upsert: [task], remove: ctx ? [ctx.tempId] : [] })
    });
}

/** Save the whole task (PUT) — optimistic. */
export function useSaveTask() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (task: TaskItem) => mailApi.updateTask(task.id, taskToPayload(task), token),
        onMutate: async (task) => {
            await qc.cancelQueries({ queryKey: ["tasks"] });
            const snapshot = snapshotTasks(qc);
            applyTaskChange(qc, { upsert: [{ ...task, updatedAt: new Date().toISOString() }] });
            return { snapshot };
        },
        onError: (_e, _t, ctx) => restoreTasks(qc, ctx?.snapshot),
        onSuccess: (saved) => applyTaskChange(qc, { upsert: [saved] })
    });
}

/** Complete / reopen. Completing a repeating task returns its next occurrence. */
export function useToggleTask() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: async ({ id, complete }: { id: string; complete: boolean }): Promise<{ task: TaskItem; next: TaskItem | null }> =>
            complete
                ? mailApi.completeTask(id, token)
                : { task: await mailApi.uncompleteTask(id, token), next: null },
        onMutate: async ({ id, complete }) => {
            await qc.cancelQueries({ queryKey: ["tasks"] });
            const snapshot = snapshotTasks(qc);
            const current = findCachedTask(qc, id);
            if (current) {
                applyTaskChange(qc, {
                    upsert: [{ ...current, isCompleted: complete, completedAt: complete ? new Date().toISOString() : null }]
                });
            }
            return { snapshot, flagged: current?.source === "flagged" };
        },
        onError: (_e, _v, ctx) => restoreTasks(qc, ctx?.snapshot),
        onSuccess: (res, _v, ctx) => {
            applyTaskChange(qc, { upsert: res.next ? [res.task, res.next] : [res.task] });
            // Completing a flagged-email task unflags the message.
            if (ctx?.flagged) invalidateMailLists(qc);
        }
    });
}

export function useDeleteTask() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteTask(id, token),
        onMutate: async (id) => {
            await qc.cancelQueries({ queryKey: ["tasks"] });
            const snapshot = snapshotTasks(qc);
            applyTaskChange(qc, { remove: [id] });
            return { snapshot };
        },
        onError: (_e, _id, ctx) => restoreTasks(qc, ctx?.snapshot)
    });
}

/** Mirror flagged messages into the Flagged email list; refreshes tasks when anything moved. */
export function useSyncFlaggedTasks() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: () => mailApi.syncFlaggedTasks(token),
        onSuccess: (r) => {
            if (r.created + r.completed + r.reopened > 0) void qc.invalidateQueries({ queryKey: ["tasks"] });
        }
    });
}

// ── Task lists ──

export function useTaskLists() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["task-lists", token],
        queryFn: () => mailApi.listTaskLists(token),
        enabled: Boolean(token),
        staleTime: 60_000
    });
}

export function useCreateTaskList() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: UpsertTaskListPayload) => mailApi.createTaskList(payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["task-lists"] })
    });
}

export function useUpdateTaskList() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: UpsertTaskListPayload }) =>
            mailApi.updateTaskList(id, payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["task-lists"] })
    });
}

export function useDeleteTaskList() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => mailApi.deleteTaskList(id, token),
        onSuccess: (_r, id) => {
            // Its tasks went with it.
            const gone: string[] = [];
            for (const [, data] of qc.getQueriesData<TaskItem[]>({ queryKey: ["tasks"] }))
                for (const t of data ?? []) if (t.listId === id) gone.push(t.id);
            applyTaskChange(qc, { remove: gone });
            void qc.invalidateQueries({ queryKey: ["task-lists"] });
        }
    });
}

export function useReorderTaskLists() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (ids: string[]) => mailApi.reorderTaskLists(ids, token),
        onMutate: async (ids) => {
            await qc.cancelQueries({ queryKey: ["task-lists"] });
            const snapshot = qc.getQueriesData<TaskList[]>({ queryKey: ["task-lists"] });
            qc.setQueriesData<TaskList[]>({ queryKey: ["task-lists"] }, (old) =>
                old
                    ? [...old]
                        .sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id))
                        .map((l, i) => ({ ...l, sortOrder: i }))
                    : old);
            return { snapshot };
        },
        onError: (_e, _ids, ctx) => {
            for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data);
        },
        onSuccess: (lists) => qc.setQueriesData<TaskList[]>({ queryKey: ["task-lists"] }, () => lists)
    });
}
